import { RouterState } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import { IntlState } from 'react-intl-redux';
import { combineReducers } from 'redux';
import localforage from 'localforage';
import { keycloakReducer, KeycloakState } from './keycloak';
import { createReduxHistoryContext } from 'redux-first-history';
import { configureStore } from '@reduxjs/toolkit';
import { productsReducer, ProductsState } from './products';
import { thunk } from 'redux-thunk';
import logger from 'redux-logger';
import { getConfig } from '../config';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { sideActions } from '../middlewares';
import { locationsReducer, LocationsState } from './search';
import { UiReducer, UIState } from './ui';
import de from '../translations/de.json';
import it from '../translations/it.json';
import fr from '../translations/fr.json';
import en from '../translations/en.json';

const lang = (window as any)['django']?.language || sessionStorage.getItem('apg_dc_language') || 'de';
const messages = {
  en: en,
  de: de,
  it: it,
  fr: fr,
};

const i18nInitialState = {
  locale: lang,
  messages: {
    ...messages[lang],
  },
};

const i18nReducer = (state = i18nInitialState) => state;

export interface AppState {
  intl: IntlState;
  keycloak: KeycloakState;
  router: RouterState;
  products: ProductsState;
  locations: LocationsState;
  ui: UIState;
}

const { routerMiddleware, routerReducer, createReduxHistory } = createReduxHistoryContext({
  history: createBrowserHistory(),
});

function loadState(): any {
  try {
    const serializedState = localStorage.getItem('appState');
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    console.error('Could not load state from localStorage:', err);
    return undefined;
  }
}

let middlewares: any[];

if (getConfig('isLive')) {
  Sentry.init({
    dsn: 'https://cbb71537e3f14a6da6191e9efb1d8073@o162224.ingest.sentry.io/5505698',
    integrations: [new Integrations.BrowserTracing()],
    beforeSend(event, hint) {
      // Check if it is an exception, and if so, show the report dialog
      console.warn('Will report error event to sentry', event, 'hint', hint);
      if (event.exception) {
        Sentry.showReportDialog({ eventId: event.event_id });
      }
      return event;
    },
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.01,
  });

  middlewares = [routerMiddleware, thunk, sideActions];
} else {
  middlewares = [routerMiddleware, thunk, sideActions, logger];
}
const preloadedState = loadState();

export const store = configureStore({
  reducer: combineReducers({
    keycloak: keycloakReducer,
    router: routerReducer,
    intl: i18nReducer,
    products: productsReducer,
    locations: locationsReducer,
    ui: UiReducer,
  }),
  middleware: middlewares,
  preloadedState,
});

store.subscribe(() => {
  try {
    const { keycloak, intl, router, ...stateToSave } = store.getState();
    const serializedState = JSON.stringify(stateToSave);
    localforage.setItem('appState', serializedState);
  } catch (err) {
    console.error('Could not save state to sessionStorage:', err);
  }
});

export const history = createReduxHistory(store);
